import React, { ReactNode } from "react"
import {
  Box, BoxProps, chakra, Flex, Heading, Stack, Text,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"

type Props = {
  title?: string
  action?: ReactNode
  children: ReactNode
} & BoxProps

const DashboardCard = ({
  title, action, children, ...props
}: Props) => (
  <Flex
    as="section"
    direction="column"
    bg="white"
    borderWidth={1}
    borderColor="gray.300"
    borderRadius="md"
    p={6}
    minW={0}
    h="full"
    {...props}
  >
    {(title || action) && (
      <Flex justify="space-between" align="flex-start" gap={3} rowGap={2} wrap="wrap" mb={4}>
        {title && <Heading fontSize="lg" fontWeight="semibold" whiteSpace="nowrap">{title}</Heading>}
        {action}
      </Flex>
    )}
    {children}
  </Flex>
)

const LINK_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1.5,
  fontWeight: "semibold",
  color: "blue.600",
  _hover: { color: "blue.700" },
} as const

const CHEVRON = <Icon icon="next-arrow" size={2.5} ml={-0.5} data-testid="link-action-chevron" />

// A button that looks like a LinkAction with a chevron; as="span" inside another button.
export const LinkButton = ({ children, onClick, as = "button" }: {
  children: ReactNode, onClick?: () => void, as?: "button" | "span"
}) => (
  <chakra.button
    as={as}
    {...as === "button" && { type: "button" }}
    onClick={onClick}
    {...LINK_STYLE}
    borderRadius="sm"
    _focusVisible={{ boxShadow: "outline", outline: "none" }}
  >
    {children}
    {CHEVRON}
  </chakra.button>
)

type EmptyStateProps = {
  title: string
  body: string
  cta?: string
  onClick?: () => void
  action?: ReactNode
}

export const EmptyState = ({
  title, body, cta, onClick, action,
}: EmptyStateProps) => (
  <Stack
    align="flex-start"
    justify="center"
    textAlign="left"
    spacing={1}
    borderWidth={1}
    borderStyle="dashed"
    borderRadius="lg"
    px={6}
    py={6}
    flex={1}
  >
    <Text fontWeight="semibold">{title}</Text>
    <Text color="gray.600">{body}</Text>
    {action && <Box pt={2}>{action}</Box>}
    {cta && onClick && (
      <Box pt={2}>
        <LinkButton onClick={onClick}>{cta}</LinkButton>
      </Box>
    )}
  </Stack>
)

type LinkActionProps = {
  icon?: string
  chevron?: boolean
  children: ReactNode
  onClick?: () => void
  to?: string
  href?: string
}

// An onClick prop, even undefined, would replace Link's router navigation for `to`.
export const LinkAction = ({
  icon, chevron, children, onClick, to, href,
}: LinkActionProps) => (
  <Link
    to={to}
    href={href}
    isExternal={Boolean(href)}
    {...onClick ? { onClick: (e) => { e.preventDefault(); onClick() } } : {}}
    {...LINK_STYLE}
  >
    {icon && <Icon icon={icon} size={4} />}
    {children}
    {chevron && CHEVRON}
  </Link>
)

export default DashboardCard
